import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../api/client";
import { ResourceListItem } from "../types";

function getAnswerTypeLabel(value: unknown) {
  const labels: Record<string, string> = {
    text: "Short Text",
    textarea: "Long Text",
    number: "Number",
    date: "Date",
    yes_no: "Yes / No"
  };

  return labels[String(value || "textarea")] || String(value || "Long Text");
}

function AnswerSpace({ answerType }: { answerType: unknown }) {
  const type = String(answerType || "textarea");

  if (type === "yes_no") {
    return (
      <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
        <Typography variant="body2">Yes [ ]</Typography>
        <Typography variant="body2">No [ ]</Typography>
      </Stack>
    );
  }

  if (type === "date") {
    return <Box sx={{ mt: 1, width: 220, borderBottom: "1px solid #9c8877", height: 28 }} />;
  }

  const lineCount = type === "textarea" ? 3 : 1;

  return (
    <Stack spacing={1} sx={{ mt: 1.25 }}>
      {Array.from({ length: lineCount }).map((_, index) => (
        <Box key={index} sx={{ borderBottom: "1px solid #9c8877", height: 26 }} />
      ))}
    </Stack>
  );
}

export default function CustomQuestionsPrintPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<ResourceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await adminApi.listResource<ResourceListItem>(
          "custom-questions",
          "?limit=500&sort=display_order&order=ASC"
        );
        setQuestions(response.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load custom questions");
      } finally {
        setLoading(false);
      }
    };

    void loadQuestions();
  }, []);

  const activeQuestions = useMemo(() => (
    questions.filter((question) => question.is_active === undefined || question.is_active === true || question.is_active === 1)
  ), [questions]);

  return (
    <Stack spacing={2.5} sx={{ pb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        className="print-hidden"
      >
        <Button variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/admin/custom-questions")}>
          Back
        </Button>
        <Button variant="contained" startIcon={<PrintRoundedIcon />} onClick={() => window.print()}>
          Print
        </Button>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && !error ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            border: "1px solid rgba(118, 93, 68, 0.16)",
            background: "#fffaf4",
            "@media print": {
              boxShadow: "none",
              border: 0,
              p: 0
            }
          }}
        >
          <style>
            {`
              @media print {
                body { background: #fff !important; }
                .print-hidden { display: none !important; }
              }
            `}
          </style>

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              EVENTENA
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Custom Questions
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>
              Planning questions for customer discovery and event requirement collection.
            </Typography>
          </Stack>

          <Divider sx={{ mb: 2.5 }} />

          <Stack spacing={2.25}>
            {activeQuestions.length ? activeQuestions.map((question, index) => (
              <Box
                key={String(question.id || index)}
                sx={{
                  breakInside: "avoid",
                  p: 2,
                  border: "1px solid rgba(118, 93, 68, 0.16)",
                  borderRadius: 2,
                  background: "rgba(255, 252, 248, 0.8)",
                  "@media print": {
                    borderColor: "#c8b6a6",
                    background: "#fff"
                  }
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 800, minWidth: 28 }}>
                    {index + 1}.
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Typography sx={{ fontWeight: 800 }}>
                        {String(question.question_text || "")}
                      </Typography>
                      {question.is_required ? <Chip size="small" label="Required" color="warning" /> : null}
                      <Chip size="small" label={getAnswerTypeLabel(question.answer_type)} variant="outlined" />
                    </Stack>
                    {question.help_text ? (
                      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                        {String(question.help_text)}
                      </Typography>
                    ) : null}
                    <AnswerSpace answerType={question.answer_type} />
                  </Box>
                </Stack>
              </Box>
            )) : (
              <Typography sx={{ color: "text.secondary" }}>
                No active custom questions found.
              </Typography>
            )}
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}
